/* 郭脩（又作郭循，字孝先）
 * v0.5.0 新武將：用「能」（docs/ENERGY-SURVEY.md §6）；技能 v1 照 LoL 劫（耗能減半、射程 ×0.8）。
 * 史實：被姜維擄去後假意投降，延熙十六年歲首大會上刺殺蜀大將軍費禕，魏追封長樂鄉侯。 */
import { SG, gd, landing, move, spMul } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, U, P, S, D } from '../define.ts';

const ID = 'guoxiu';
// 影子：u.gxS = [{ x, y, until, k: 'w' | 'r' }]（只是位置，不是單位：打不到、不擋路、不給視野）
const SHADOW_R = 140; // 影子普攻的距離（影子不會走過去）
const shadows = (w: any, u: Unit) => (u.gxS || []).filter((s: any) => s.until > w.t);
const castFrom = (w: any, u: Unit) => [{ x: u.x, y: u.y, me: true }].concat(shadows(w, u).map((s: any) => ({ x: s.x, y: s.y, me: false })));
// 同一招本體和影子都打中同一名敵將：回 15 能（每招一次）
function doubleHit(w: any, u: Unit, seen: Map<number, Set<boolean>>, t: Unit, me: boolean, cast: any) {
  if (t.kind !== 'hero') return;
  const s = seen.get(t.id) || new Set<boolean>(); s.add(me); seen.set(t.id, s);
  if (s.size === 2 && !cast.refunded) { cast.refunded = true; u.mp = Math.min(u.maxMp, u.mp + 15); }
}
// 電腦：詐降好了、還沒有影子（先放影子再丟袖刃）
const wReady = (w: any, u: Unit) => u.skillLv[1] > 0 && u.cd[1] <= 0 && !(u.rc && u.rc[1]) && u.mp >= 20 + 38;
// 跟影子換位置
function swap(w: any, u: Unit, k: string) {
  const s = shadows(w, u).find((x: any) => x.k === k);
  if (!s) return;
  const x = u.x, y = u.y;
  w.fx({ k: 'puff', x, y }); u.x = s.x; u.y = s.y; s.x = x; s.y = y; w.fx({ k: 'puff', x: u.x, y: u.y });
}

export default defineHero({
  id: ID, name: '郭脩', sub: '字孝先', faction: 'wei', role: '刺客', weapon: 'sword', res: 'neng',
  look: {
    knife: true,
    dual: true, weaponName: '袖中雙刃', head: 'hood', eye: 'narrow', beard: null, skin: '#e0b48c', hair: '#141216', fx: '#b38cff',
    c: { armor: '#2a2630', sleeve: '#3a3346', cloth: '#231f2a', trim: '#8a6ad0', pants: '#1c1a20', boot: '#141216', cape: '#3b2f52', belt: '#6b4a8a', metal: '#cfd2e0', plume: '#6b4a8a' },
  },
  passive: ['伺機', '普攻打生命低於 50% 的敵人，額外造成其最大生命 {6/8/10}% 的法術傷害，同一目標 10 秒一次。', { lv: [1, 7, 17] }],
  skills: [
    ['袖刃', 'bolt', '本體朝游標方向擲出一枚飛刃，影子朝游標那一點擲出一枚，各飛 720，貫穿沿路的敵人：第一個 {80/127/173/220} 物理傷害，之後的 60%。',
      { x: { k: 'gxShuriken', dmg: [80, 127, 173, 220], speed: 1360, at: 0.3 }, aim: D(720, 30), shape: 'knife', cd: 6, mp: 38, el: null }],
    ['詐降', 'dash', '指定一個地點（520 內），影子衝到那裡停留 5 秒，不會移動，會以郭脩攻擊力的 50% 普攻身邊的敵人；本體與影子都打中同一名敵將時回 15 能。5 秒內再按一次：「換影」。',
      { x: { k: 'gxShadow', dur: 5.25, at: 0.05 }, aim: P(520, 40), cd: [20, 18, 16, 14], mp: 20, recast: 5.25, el: null,
        then: ['換影', 'dash', '跟「詐降」的影子交換位置。', { x: { k: 'gxSwapW', at: 0.02 }, aim: S(0), mp: 0, el: null }] }],
    ['亂刃', 'spin', '本體與影子揮刀繞身一圈，周圍 230 的敵人立即受到 {70/97/123/150} 物理傷害；被影子打中的緩速 {20/27/33/40}% 1.5 秒。每打中一名敵將，「詐降」冷卻 -2 秒。不必停下：邊走邊放、不打斷普攻。',
      { x: { k: 'gxSlash', dmg: [70, 97, 123, 150], slow: [20, 27, 33, 40], r: 230 }, aim: S(230), free: true, cd: 5, mp: 25, el: null }],
    ['歲首刺', 'blink', '無法被選取（可藉此躲技能），在原地留下影子、閃到一名敵將背後（他面向的反方向），標記他 3 秒；時間到時造成一次攻擊力 100% 的物理傷害，加上標記期間你對他造成傷害的 {25/40/55}%。0.5 秒後、6 秒內再按一次：「脫身」。',
      { x: { k: 'gxMark', pct: [25, 40, 55], dur: 3, at: 0.05 }, aim: U(500), cd: [120, 90, 60], mp: 0, recast: 6.5, el: null, shout: '歲首之會，正是此時！',
        then: ['脫身', 'dash', '跟「歲首刺」的影子交換位置。', { x: { k: 'gxSwapR', at: 0.02 }, aim: S(0), mp: 0, el: null }] }],
  ],
  kinds: {
    // 詐降（影分身）：點地，影子衝到那個點（a.pt 已經限制在射程內）
    gxShadow(u, a, X) {
      const to = landing(u.x, u.y, a.pt[0], a.pt[1]);
      const s: any = { x: u.x, y: u.y, until: this.t + X.dur, k: 'w' };
      u.gxS = shadows(this, u).filter((o: any) => o.k !== 'w').concat([s]);
      const n = 10;
      for (let j = 1; j <= n; j++) this.later(0.035 * j, () => { s.x += (to[0] - s.x) / (n - j + 1); s.y += (to[1] - s.y) / (n - j + 1); });
    },
    gxSwapW(u) { swap(this, u, 'w'); },
    gxSwapR(u) { swap(this, u, 'r'); },
    // 袖刃（影手裡劍）：本體朝方向丟，影子朝游標那一點丟（跟主人同一個目標），各飛滿射程，貫穿；同一個人被第二枚打中只吃一半
    gxShuriken(u, a, X) {
      const sp = spMul(u), R = this.skillRange(a.sk, a.lvl), pt = a.pt;
      const seen = new Map<number, Set<boolean>>(), hitN = new Map<number, number>(), cast: any = {};
      for (const o of castFrom(this, u)) {
        const dir = o.me ? a.dir : SG.dirTo(o, pt[0], pt[1]), range = R;
        let n = 0;
        this.shootProj(u, { x: o.x + dir[0] * 30, y: o.y + dir[1] * 30 * SG.K, dir, speed: X.speed, range, rad: 26, shape: 'knife', col: u.spec.fx, big: true,
          onHit: (t: Unit) => {
            if (t.structure) return false;
            const prev = hitN.get(t.id) || 0; hitN.set(t.id, prev + 1);
            this.damage(u, t, X.dmg * sp * (n++ ? 0.6 : 1) * (prev ? 0.5 : 1), { stagger: 0.2, sk: true });
            doubleHit(this, u, seen, t, o.me, cast);
            return false;
          } });
      }
    },
    // 亂刃（影奪）：本體與影子周圍，同一個人只吃一次；被影子打中的緩速；每名敵將讓詐降冷卻 -2 秒
    // 亂刃：免動作技（free），本體與影子各以自己為中心立即打一圈
    gxSlash(u, a, X) {
      const sp = spMul(u), done = new Set<number>(), seen = new Map<number, Set<boolean>>(), cast: any = {};
      for (const o of castFrom(this, u)) {
        this.fx({ k: 'bladering', x: o.x, y: o.y, r: X.r, col: u.spec.fx });
        for (const t of this.enemiesNear(u, o.x, o.y, X.r)) {
          if (t.structure) continue;
          if (!o.me && t.alive) this.addBuff(t, { id: a.sk.name, t: 1.5, neg: true, ms: -X.slow / 100 });
          doubleHit(this, u, seen, t, o.me, cast);
          if (done.has(t.id)) continue;
          done.add(t.id);
          this.damage(u, t, X.dmg * sp, { stagger: 0.25, sk: true });
          if (t.kind === 'hero') { const r = u.rc && u.rc[1]; if (r && r.alt) r.cd0 = Math.max(0, r.cd0 - 2); else u.cd[1] = Math.max(0, u.cd[1] - 2); }
        }
      }
    },
    // 歲首刺（死亡印記）：無法被選取、閃到身後、留影子、標記 3 秒後結算
    gxMark(u, a, X) {
      const t = this.xPick(u, a.pt, 150);
      if (!t || t.kind !== 'hero') { this.xRefund(u, a); if (u.rc) u.rc[3] = null; return; }
      u.gxS = shadows(this, u).filter((o: any) => o.k !== 'r').concat([{ x: u.x, y: u.y, until: this.t + 6.5, k: 'r' }]);
      u.untargetable = true; u.air = true;
      if (u.rc && u.rc[3]) u.cd[3] = Math.max(u.cd[3], 0.5); // 「脫身」要等閃到敵人背後之後才能按
      this.later(0.5, () => {
        u.untargetable = false; u.air = false;
        if (!u.alive || !t.alive) return;
        const back = -(t.f || 1), q = landing(u.x, u.y, t.x + back * (t.r + u.r + 6), t.y); // 永遠出現在他面向的反方向
        u.x = q[0]; u.y = q[1]; u.f = -back;
        const m = { by: u.id, sum: 0, until: this.t + X.dur, pct: X.pct / 100, atk: u.atk };
        t.gxMark = m;
        this.fx({ k: 'spark', x: t.x, y: t.y - 40, col: u.spec.fx });
        this.later(X.dur, () => {
          if (t.gxMark !== m) return;
          t.gxMark = null;
          if (!t.alive) return;
          this.damage(u, t, m.atk + m.sum * m.pct, { stagger: 0.5, sk: true });
          this.fx({ k: 'boom', x: t.x, y: t.y, r: 90, col: u.spec.fx });
        });
      });
      this.fx({ k: 'puff', x: u.x, y: u.y });
    },
  },
  hooks: [
    // 歲首刺：記下標記期間對他造成的傷害
    hook('damage.after', 25, function (c, lost) {
      const m = c.t.gxMark;
      if (m && m.by === c.own.id && lost > 0 && m.until >= this.t) m.sum += lost;
    }),
    // 伺機（藐視弱者）：普攻打半血以下的敵人，額外最大生命 % 法術傷害，同一目標 10 秒一次
    hook('onHit', 22, function (u, t) {
      if (u.spec.id !== ID || t.structure || t.hp / t.maxHp >= 0.5) return;
      const cd = u.gxCt || (u.gxCt = {});
      if ((cd[t.id] || 0) > this.t) return;
      cd[t.id] = this.t + 10;
      const p = [0.06, 0.08, 0.1][SG.talTier(u.spec, u.lv)];
      this.damage(u, t, t.maxHp * p * (t.kind === 'hero' ? 1 : 0.5), { magic: true, quiet: true });
      this.fx({ k: 'spark', x: t.x, y: t.y - 30, col: u.spec.fx });
    }),
    hook('revive', 20, function (u) { u.gxS = null; }),
    // 影子不會動，但會以郭脩攻擊力的 50% 普攻身邊的敵人（優先打武將；不打建築）
    hook('tick', 40, function (u) {
      if (u.spec.id !== ID || !u.alive || !u.gxS) return;
      for (const s of shadows(this, u)) {
        if ((s.atkT || 0) > this.t) continue;
        const near = this.enemiesNear(u, s.x, s.y, SHADOW_R).filter((t: Unit) => !t.structure && t.kind !== 'ward');
        if (!near.length) continue;
        const t = near.find((x: Unit) => x.kind === 'hero') || near.sort((p: Unit, q: Unit) => gd(s.x, s.y, p.x, p.y) - gd(s.x, s.y, q.x, q.y))[0];
        s.atkT = this.t + 1.15 * 100 / SG.effAsp(u);
        const f = Math.sign(t.x - s.x) || 1;
        this.damage(u, t, u.atk * 0.5, { stagger: 0.1 });
        this.fx({ k: 'crescent', x: t.x - f * 20, y: t.y, vx: f * 60, vy: 0, f, col: u.spec.fx });
      }
    }),
  ],
  // 電腦：先放影子再丟袖刃、亂刃；殘血敵將就用歲首刺，打完或自己殘血就換回影子
  ai: {
    gxShadow: ({ t, d, hero }) => hero && d < 700,
    gxSwapW: ({ w, u, t, hpR }) => { const s = shadows(w, u).find((x: any) => x.k === 'w'); if (!s) return false; return hpR < 0.3 || (!!t && t.kind === 'hero' && t.hp / t.maxHp < 0.35 && gd(s.x, s.y, t.x, t.y) < gd(u.x, u.y, t.x, t.y) - 100); },
    gxShuriken: ({ w, u, t, d, hero }) => !!t && (d < 700 || shadows(w, u).some((s: any) => gd(s.x, s.y, t.x, t.y) < 700)) && (hero || u.mp > 60) && !(hero && wReady(w, u)), // 打武將先放影子
    gxSlash: ({ w, u, t, hero }) => !!t && castFrom(w, u).some((o: any) => gd(o.x, o.y, t.x, t.y) < 220) && (hero || u.mp > 50),
    gxMark: ({ t, d, hero }) => hero && d < 500 && t!.hp / t!.maxHp < 0.65,
    gxSwapR: ({ hpR }) => hpR < 0.4,
  },
});
